import {useCallback, useMemo, useState} from 'react'
import {useSearchParams} from 'react-router-dom'
import {ClipboardList} from 'lucide-react'
import type {ComponentType} from 'react'
import {EmptyState} from '@/components/shared/EmptyState'
import {PageLayout} from '@/components/shared/PageLayout'
import type {TabDefinition} from '@/components/shared/PageLayout'
import {CONCEPT_ICON} from '@/lib/concepts'
import {permissions} from '@/lib/permissions'
import {useAuth} from '@/contexts/auth-context'
import {useI18n} from '@/i18n'
import {usePageTitle} from '@/hooks/use-page-title'
import {useAppConfig, labelOf} from '@/hooks/use-app-config'
import {
    MyResponsibilitiesTabId,
} from '@/testids/MyResponsibilitiesTestIds'
import {AccessProfiles} from '@/pages/AccessProfiles'
import {FunctionGroups} from '@/pages/FunctionGroups'
import {Resources} from '@/pages/Resources'
import {Systems} from '@/pages/Systems'
import {RestrictionRules} from '@/pages/RestrictionRules'
import {AutoAssignRules} from '@/pages/AutoAssignRules'
import {CertificationPolicies} from '@/pages/CertificationPolicies'
import {CertificationCampaigns} from '@/pages/CertificationCampaigns'

interface EmbeddedListProps {
    embedded?: boolean
    ownedByMe?: boolean
    emptyStateTitle?: string
}

interface ResponsibilityTab {
    id: typeof MyResponsibilitiesTabId[keyof typeof MyResponsibilitiesTabId]
    domainEntityTypeKey: string
    emptyKey: keyof ReturnType<typeof useI18n>['myResponsibilities']
    permission: string
    Page: ComponentType<EmbeddedListProps>
}

const TABS: readonly ResponsibilityTab[] = [
    {
        id: MyResponsibilitiesTabId.AccessProfiles,
        domainEntityTypeKey: 'ACCESS_PROFILES',
        emptyKey: 'emptyAccessProfiles',
        permission: permissions.accessProfiles.view,
        Page: AccessProfiles,
    },
    {
        id: MyResponsibilitiesTabId.FunctionGroups,
        domainEntityTypeKey: 'FUNCTION_GROUPS',
        emptyKey: 'emptyFunctionGroups',
        permission: permissions.functionGroups.view,
        Page: FunctionGroups,
    },
    {
        id: MyResponsibilitiesTabId.Resources,
        domainEntityTypeKey: 'RESOURCES',
        emptyKey: 'emptyResources',
        permission: permissions.resources.view,
        Page: Resources,
    },
    {
        id: MyResponsibilitiesTabId.TargetSystems,
        domainEntityTypeKey: 'TARGET_SYSTEMS',
        emptyKey: 'emptyTargetSystems',
        permission: permissions.systems.view,
        Page: Systems,
    },
    {
        id: MyResponsibilitiesTabId.RestrictionRules,
        domainEntityTypeKey: 'RESTRICTION_RULES',
        emptyKey: 'emptyRestrictionRules',
        permission: permissions.restrictionRules.view,
        Page: RestrictionRules,
    },
    {
        id: MyResponsibilitiesTabId.AutoAssignRules,
        domainEntityTypeKey: 'AUTO_ASSIGN_RULES',
        emptyKey: 'emptyAutoAssignRules',
        permission: permissions.autoAssignRules.view,
        Page: AutoAssignRules,
    },
    {
        id: MyResponsibilitiesTabId.CertificationPolicies,
        domainEntityTypeKey: 'CERTIFICATION_POLICIES',
        emptyKey: 'emptyCertificationPolicies',
        permission: permissions.certificationPolicies.view,
        Page: CertificationPolicies,
    },
    {
        id: MyResponsibilitiesTabId.CertificationCampaigns,
        domainEntityTypeKey: 'CAMPAIGNS',
        emptyKey: 'emptyCertificationCampaigns',
        permission: permissions.certifications.view,
        Page: CertificationCampaigns,
    },
] as const

export function MyResponsibilities() {
    const i18n = useI18n()
    const {hasPermission} = useAuth()
    const {domainEntityTypes} = useAppConfig()
    usePageTitle(i18n.myResponsibilities.pageTitle)
    const [searchParams, setSearchParams] = useSearchParams()

    const visibleTabs = useMemo(
        () => TABS.filter((tab) => hasPermission(tab.permission)),
        [hasPermission],
    )

    const tabParam = searchParams.get('tab')
    const defaultTab = visibleTabs[0]?.id ?? MyResponsibilitiesTabId.AccessProfiles
    const initialTab =
        tabParam && visibleTabs.some((t) => t.id === tabParam)
            ? tabParam
            : defaultTab

    const [activeTab, setActiveTabState] = useState<string>(initialTab)

    const setActiveTab = useCallback(
        (tab: string) => {
            setActiveTabState(tab)
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev)
                next.set('tab', tab)
                return next
            })
        },
        [setSearchParams],
    )

    const tabDefinitions: TabDefinition[] = visibleTabs.map((tab) => ({
        key: tab.id,
        label: labelOf(domainEntityTypes, tab.domainEntityTypeKey),
    }))

    if (visibleTabs.length === 0) {
        return (
            <PageLayout
                variant="list"
                icon={CONCEPT_ICON.ownership}
                title={String(i18n.myResponsibilities.title)}
                description={String(i18n.myResponsibilities.subtitle)}
                data-testid="my-responsibilities-outer-tabs"
            >
                <EmptyState
                    icon={ClipboardList}
                    title={String(i18n.myResponsibilities.noVisibleTabs)}
                />
            </PageLayout>
        )
    }

    const activeTabDefinition = visibleTabs.find((t) => t.id === activeTab) ?? visibleTabs[0]
    const emptyTitle = String(i18n.myResponsibilities[activeTabDefinition.emptyKey])
    const ActivePage = activeTabDefinition.Page

    return (
        <PageLayout
            variant="tabbed-list"
            icon={CONCEPT_ICON.ownership}
            title={String(i18n.myResponsibilities.title)}
            description={String(i18n.myResponsibilities.subtitle)}
            tabs={tabDefinitions}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            data-testid="my-responsibilities-outer-tabs"
        >
            <ActivePage embedded ownedByMe emptyStateTitle={emptyTitle}/>
        </PageLayout>
    )
}
